import { Color, Spacing } from '@persona-id/design-tokens';
import { get } from 'lodash/fp';
import { FC, Fragment, memo, useCallback, useMemo } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import { formatDisplayValue } from 'dashboard/components/JsonSchemaSelect';
import RulePill from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/RulePill';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  BinaryOperator,
  ConditionalRule,
  getConditionalPathDisplayText,
  getConditionalRuleParts,
  getRuleDisplayValue,
  JsonLogicValue,
  LogicBlockOrV2,
  UsableObjectConfig,
  valueTransformer,
} from 'dashboard/lib/workflows';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowRunStepConditional } from 'dashboard/models/workflow-step';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { JsonSchema } from 'lib/json-schema';
import { TokensToNode } from 'lib/workflows';

const AndRulesContainer = styled.div<{
  chosen: boolean;
  hasWorkflowRunStep: boolean;
  inInquiryInspectRunPath: boolean;
}>`
  ${({ inInquiryInspectRunPath }) =>
    inInquiryInspectRunPath &&
    css`
      border: ${Color.CoreBlue700} solid 2px;
    `};
  background-color: ${({ chosen, hasWorkflowRunStep }) =>
    !hasWorkflowRunStep || chosen ? Color.CoreSlate50 : Color.CoreSlate50 + '90'};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]};
  display: flex;
  flex-direction: column;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 15% 85%;
`;

const StyledParagraph = styled(Paragraph)`
  padding-top: 13px;
  padding-bottom: ${Spacing[2]};
  padding-left: ${Spacing[1]};
  font-size: 13px;
  line-height: ${Spacing[4]};
`;

const MatchedPill = styled.span<{ matched: boolean }>`
  background-color: ${({ matched }) => (matched ? Color.CorePurple200 : Color.CoreSlate100)};
  border-radius: ${BORDER_RADIUSES.rounded};
  display: inline-flex;
  width: fit-content;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  ${paragraphMixins.sm};
  margin: 2px;
  margin-left: ${Spacing[2]};
  padding: 2px ${Spacing[1]};
`;

const DataTitleContainer = styled.div`
  display: flex;
  align-items: center;
  line-height: 2;
  font-size: ${({ theme }) => theme.text.paragraph.lgBold.fontSize};
  font-weight: ${({ theme }) => theme.text.paragraph.lgBold.fontWeight};
  border-bottom: 1px solid ${Color.CoreSlate50};
`;

const DataPair = styled.div`
  margin-top: ${Spacing[3]};
  margin-bottom: ${Spacing[3]};
  margin-left: ${Spacing[1]};
`;

const DataKey = styled(Paragraph)`
  font-size: 13px;
`;

const DataValue = styled(Paragraph)`
  font-size: 13px;
  padding-top: ${Spacing[1]};
`;

interface RulesSectionProps {
  editable?: boolean;
  errors: WorkflowError[];
  jsonSchema: JsonSchema;
  rootPathForField?: string;
  routeIndex: number;
  rules: LogicBlockOrV2;
  tokensToDisplayValue: Record<string, string>;
  tokensToSteps: TokensToNode;
  usableObjects: UsableObjectConfig[];
  workflowRunStep?: WorkflowRunStepConditional;
  inInquiryInspectRunPath?: boolean;
}

type RunData = {
  displayValue: JsonLogicValue;
  keyName: string;
};

type RuleWithData = {
  andRules: ConditionalRule[];
  chosen: boolean;
  matched: boolean;
  runData: Record<string, RunData> | null;
};

const RulesSection: FC<RulesSectionProps> = ({
  editable = true,
  errors,
  jsonSchema,
  rootPathForField = '',
  routeIndex,
  rules,
  tokensToDisplayValue,
  tokensToSteps,
  usableObjects,
  inInquiryInspectRunPath,
  workflowRunStep = null,
}) => {
  const chosenOrIndices = useMemo(
    () => workflowRunStep?.attributes?.['chosen-or-indices'] ?? [],
    [workflowRunStep],
  );
  const chosenRoute = workflowRunStep?.attributes?.['chosen-conditional-index'];
  const conditionalData = useMemo(
    () => JSON.parse(workflowRunStep?.attributes?.['conditionals-json'] ?? 'null'),
    [workflowRunStep],
  );

  const getRunDataForPath = useCallback(
    (path: string): RunData => ({
      displayValue: getRuleDisplayValue(
        get(path, conditionalData[routeIndex].vars),
        valueTransformer(tokensToDisplayValue).getDisplayValue(path),
        jsonSchema,
      ),
      keyName: getConditionalPathDisplayText(
        path,
        usableObjects,
        jsonSchema,
        tokensToSteps,
        tokensToDisplayValue,
      ),
    }),
    [conditionalData, routeIndex, tokensToDisplayValue, jsonSchema, usableObjects, tokensToSteps],
  );

  const rulesWithData: RuleWithData[] = useMemo(
    () =>
      rules[BinaryOperator.Or].map((andRulesConfig, i) => {
        const andRules = andRulesConfig[BinaryOperator.And];
        const withRunData =
          !editable && conditionalData != null && conditionalData[routeIndex]?.result != null;

        return {
          andRules,
          chosen: chosenOrIndices.includes(i) && chosenRoute === routeIndex,
          matched: withRunData && conditionalData[routeIndex]?.meta?.or_indices?.includes(i),
          runData: withRunData
            ? andRules.reduce((hash: Record<string, RunData>, andRule: ConditionalRule) => {
                const { valueLeftConfig, valueRightConfig } = getConditionalRuleParts(andRule);

                valueLeftConfig?.paths.forEach((path) => {
                  hash[path] = getRunDataForPath(path);
                });
                valueRightConfig?.paths.forEach((path) => {
                  hash[path] = getRunDataForPath(path);
                });

                return hash;
              }, {})
            : null,
        };
      }),
    [chosenOrIndices, chosenRoute, conditionalData, editable, routeIndex, rules, getRunDataForPath],
  );

  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError(errors);

  return (
    <>
      {rulesWithData.map(({ chosen, andRules, runData, matched }, i) => {
        return (
          <Fragment key={i}>
            {i !== 0 && <Spacer height={2} />}
            <AndRulesContainer
              chosen={chosen}
              hasWorkflowRunStep={!!workflowRunStep}
              inInquiryInspectRunPath={!!inInquiryInspectRunPath}
            >
              {andRules.map((andRule, j) => (
                <GridRow key={j}>
                  <StyledParagraph size='sm'>
                    {j === 0 ? (i === 0 ? 'Where' : 'Or') : 'And'}
                  </StyledParagraph>
                  <div>
                    <RulePill
                      andRule={andRule}
                      hasError={hasErrorFor(`${rootPathForField}rules.or[${i}].and[${j}]`)}
                      rejected={!chosen}
                      usableObjects={usableObjects}
                      tokensToSteps={tokensToSteps}
                      jsonSchema={jsonSchema}
                      tokensToDisplayValue={tokensToDisplayValue}
                    />
                    {renderErrorMessageFor(`${rootPathForField}rules.or[${i}].and[${j}]`, {
                      narrow: true,
                    })}
                  </div>
                </GridRow>
              ))}
              {!!runData && (
                <>
                  <Spacer height={2} />
                  <Accordion
                    title={
                      <DataTitleContainer>
                        Data
                        <MatchedPill matched={matched}>
                          {matched ? 'Matched' : 'No Match'}
                        </MatchedPill>
                      </DataTitleContainer>
                    }
                    menuItemBorder={false}
                    menuItemHover={false}
                    padded={false}
                    disabledTheme='primary'
                    disabledHideCaret
                    menuItem
                    defaultExpanded={false}
                  >
                    <div aria-label={`Data to evaluate Route ${routeIndex + 1}, Rule ${i + 1}`}>
                      {Object.values(runData).map(({ displayValue, keyName }, j) => (
                        <DataPair key={j}>
                          <DataKey size='sm' color='tertiary'>
                            {keyName}
                          </DataKey>
                          <DataValue size='sm' color='primary'>
                            {formatDisplayValue(displayValue)}
                          </DataValue>
                        </DataPair>
                      ))}
                    </div>
                  </Accordion>
                </>
              )}
            </AndRulesContainer>
          </Fragment>
        );
      })}
    </>
  );
};

export default memo(RulesSection);
